/**
 * Contrato de mensajes del puente builder (app del cliente ↔ dashboard).
 * Ambos lados validan `event.origin` y que `token` coincida antes de actuar.
 *
 * Flujo:
 *   1. El dashboard abre la app con `?veoBuilder=<token>&veoBuilderOrigin=<origen>`.
 *   2. La app (veo-builder.js) postea `ready` a la ventana que la abrió.
 *   3. El dashboard manda comandos (`start-picker`, `preview`, …).
 *   4. La app responde con eventos (`picked`, `pick-cancelled`, …).
 */
import type { PreviewGuideInput } from '../guides/guide-preview';
import type { BlockDropPayload } from './block-drop-target';
import type { DesignPatch } from './design-manipulator';
import type { PickedElement } from './element-picker';

export const VEO_BUILDER_SOURCE = 'veo-builder';
// Los nombres de los query params viven en el core (`builder-token.ts`), única
// fuente de verdad compartida con el loader y el gate del runtime de guías.
export { BUILDER_ORIGIN_PARAM, BUILDER_TOKEN_PARAM } from '../../core/builder-token';

/**
 * Estado que el panel (editor) publica para que el SDK pinte el "chrome" del
 * editor (topbar + fila de pasos) ENCIMA de la app. El panel es la única fuente
 * de verdad; el chrome solo lo refleja y devuelve `chrome-action`s.
 */
export interface EditorChromeState {
  guideName: string;
  /** ISO de creación (null = guía nueva sin guardar). */
  createdAt: string | null;
  steps: Array<{ id: string; label: string; presentation: 'floating' | 'inline' }>;
  activeStep: number;
  /** Qué muestra el panel: ajustes de activación o el paso activo. */
  section: 'activation' | 'step' | 'badge' | 'target';
  saving: boolean;
  dirty: boolean;
  /**
   * Estado de publicación de la guía, para que el autor vea en la barra si lo
   * que edita está Activa/Borrador/etc. (sin esto, guardar una guía en borrador
   * y no verla en la app era un misterio). `tone` elige el color del chip.
   */
  status?: { label: string; tone: 'live' | 'testing' | 'review' | 'draft' | 'paused' } | null;
}

/**
 * Qué está haciendo la preview en la app. El SDK lo publica cada vez que
 * evalúa un comando `preview` con `honorTrigger`, para que el panel explique
 * por qué la guía no se ve (retraso, elemento objetivo, otra página, otro
 * dispositivo) en vez de parecer rota.
 */
export type PreviewStatus =
  | { state: 'shown' }
  | { state: 'waiting-delay'; delayMs: number }
  | { state: 'waiting-trigger'; trigger: 'on_click' | 'on_hover'; selector: string }
  | { state: 'page-mismatch'; pattern: string }
  | { state: 'device-mismatch'; devices: 'desktop' | 'mobile' };

/** Acciones que el usuario dispara en el chrome (barras) y el panel ejecuta. */
export type ChromeAction =
  | { action: 'select-activation' }
  | { action: 'select-step'; index: number }
  | { action: 'add-step' }
  | { action: 'save'; exit: boolean }
  | { action: 'exit' }
  // Las tres siguientes las resuelve el SDK solo (no llegan al panel):
  // barra arriba/abajo, volver a mostrar la tarjeta y modo vista previa.
  | { action: 'dock'; position: 'top' | 'bottom' }
  | { action: 'show-panel' }
  | { action: 'preview' }
  | { action: 'exit-preview' };

/** Eventos que la app del cliente envía al dashboard. */
export type BuilderEvent =
  // `chrome: true` → el SDK pinta el chrome del editor (barras); el panel debe
  // ocultar su propia cabecera/pasos/guardado y publicar `editor-state`.
  | { type: 'ready'; chrome?: boolean }
  | { type: 'chrome-action'; payload: ChromeAction }
  | { type: 'picked'; payload: PickedElement }
  | { type: 'pick-cancelled' }
  // Manipulación directa: el autor arrastró/redimensionó la guía en la app;
  // el panel mergea `payload.style` sobre el `style` del paso indicado.
  | { type: 'design-updated'; payload: DesignPatch }
  // Drag & drop de bloques: el autor soltó un bloque de la paleta SOBRE la
  // guía en preview; el panel lo inserta en `step.blocks[]` en esa posición.
  | { type: 'block-dropped'; payload: BlockDropPayload }
  // Estado de la preview (ver `PreviewStatus`).
  | { type: 'preview-status'; payload: PreviewStatus }
  | { type: 'closed' };

/** Comandos que el dashboard/panel envía a la app del cliente. */
export type BuilderCommand =
  | { type: 'start-picker'; maxAncestors?: number; hint?: string }
  | { type: 'stop-picker' }
  // `editable: true` (editor in-app) adjunta drag/resize sobre la preview.
  // `honorTrigger: true`: si la guía se dispara por elemento objetivo
  // (activationRules on_click/on_hover + selector), la preview ESPERA a esa
  // interacción en la app en vez de pintarse de una.
  // `honorTrigger` también respeta «retrasar la aparición» (delayMs) y el
  // filtro de dispositivo, publicando `preview-status` con lo que está pasando.
  | { type: 'preview'; guide: PreviewGuideInput; editable?: boolean; honorTrigger?: boolean }
  // Drag de un bloque de la paleta POR POINTER (el DnD HTML5 no cruza bien el
  // iframe del panel): el panel captura el puntero y streamea sus coordenadas
  // RELATIVAS AL IFRAME; la app las convierte con el rect del panel, mueve la
  // línea de inserción y en `drop` responde con el evento `block-dropped`.
  | {
      type: 'block-drag';
      phase: 'move' | 'drop' | 'cancel';
      x: number;
      y: number;
      blockType: string;
      label?: string;
    }
  | { type: 'close-preview' }
  | { type: 'teardown' }
  // Chrome del editor: el panel publica su estado (nombre, pasos, sección…).
  | { type: 'editor-state'; state: EditorChromeState }
  // El panel pide ocultarse/mostrarse (botón «Cerrar» del contenido).
  | { type: 'panel-visibility'; visible: boolean }
  // Modo panel inyectado: el panel avisa que cargó y está listo para recibir
  // eventos; la app responde con `ready`. Resuelve la carrera de que el iframe
  // del panel todavía no tenga su listener cuando la app postea `ready`.
  | { type: 'panel-ready' };

/** Sobre común: todo mensaje del puente lleva `source` + `token`. */
export interface BuilderEnvelope {
  source: typeof VEO_BUILDER_SOURCE;
  token: string;
}

export type BuilderEventMessage = BuilderEnvelope & BuilderEvent;
export type BuilderCommandMessage = BuilderEnvelope & BuilderCommand;
